import PlayerAvatar from 'components/ui/PlayerAvatar';
import AppLightTooltip from 'components/ui/AppLightTooltip';
import AppUseIcon from 'components/ui/AppUseIcon';
import { PlayerOptionDto } from 'types/models/SessionsPageData';
import TimeframePicker from './TimeframePicker';
import { Timeframe, TimeframePreset } from './playerPerformanceUtils';
import './PlayerHeader.scss';

interface PlayerHeaderProps {
  player: PlayerOptionDto;
  /** Snapshot info shown in the ⓘ tooltip next to the name; omitted when there is no snapshot meta. */
  infoContent?: React.ReactNode;
  onFocusTeam: (teamKey: string) => void;
  timeframe: Timeframe;
  onTimeframeChange: (timeframe: Timeframe) => void;
  /** Timeframe presets that contain data for this player. */
  availablePresets?: ReadonlySet<TimeframePreset>;
}

/**
 * The page header while a player is open: avatar + name as the page title (with the snapshot info icon), the team
 * underneath, and the timeframe filter on the right. It replaces the "Player Performance" title and the separate
 * player card, so the report starts higher on the page. Back to the player list is the Player Performance nav tab.
 */
const PlayerHeader: React.FunctionComponent<PlayerHeaderProps> = ({ player, infoContent, onFocusTeam, timeframe, onTimeframeChange, availablePresets }) => (
  <div className='pp-player-header' data-test='pp-player-header'>
    <div className='pp-player-header__who'>
      <PlayerAvatar name={player.name} imageUrl={player.avatarUrl} size='lg' colorKey={player.teamName} />
      <div className='pp-player-header__text'>
        <h1 className='pp-player-header__name font-25 text-uppercase mb-0' data-test='pp-player-name'>
          {player.name}
          {infoContent ? (
            <AppLightTooltip title={infoContent}>
              <AppUseIcon name='InfoIcon' className='ml-2' />
            </AppLightTooltip>
          ) : null}
        </h1>
        <button
          type='button'
          className='pp-perf-team pp-perf-team--link'
          onClick={() => onFocusTeam(`${player.clubName}␟${player.teamName}`)}
          data-test='pp-player-team'
        >
          {player.clubName} · {player.teamName}
        </button>
      </div>
    </div>
    <TimeframePicker timeframe={timeframe} onChange={onTimeframeChange} availablePresets={availablePresets} />
  </div>
);

export default PlayerHeader;
